/* eslint-disable consistent-return */

/* eslint-disable no-param-reassign */
import { DependencyList, useEffect } from "react";

const FOCUSABLE =
  'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';

interface UseSlidesAccessibilityArgs {
  containerRef: React.RefObject<HTMLDivElement>;
  deps: DependencyList;
}

const useSlidesAccessibility = ({
  containerRef,
  deps,
}: UseSlidesAccessibilityArgs) => {
  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const slides = container.querySelectorAll(".swiper-slide");

    const setSlideAccessibility = (slide: Element, visible: boolean): void => {
      const htmlSlide = slide as HTMLElement;

      if (visible) {
        htmlSlide.removeAttribute("aria-hidden");

        htmlSlide.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
          if (el.dataset.prevTabindex) {
            el.tabIndex = Number(el.dataset.prevTabindex);
            delete el.dataset.prevTabindex;
          } else {
            el.removeAttribute("tabindex");
          }
        });
      } else {
        htmlSlide.setAttribute("aria-hidden", "true");

        htmlSlide.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
          if (!el.dataset.prevTabindex) {
            el.dataset.prevTabindex = String(el.tabIndex ?? 0);
          }
          el.tabIndex = -1;
        });
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setSlideAccessibility(entry.target, entry.isIntersecting);
        });
      },
      {
        root: container.querySelector(".swiper") as Element,
        threshold: 0.6,
      }
    );

    slides.forEach((slide) => observer.observe(slide));

    return () => {
      observer.disconnect();
    };
  }, deps);
};

export default useSlidesAccessibility;
